import * as XLSX from 'xlsx';

export const normalizeHeader = (value) => String(value ?? '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');

const CODE_WORDS = ['code', 'barcode', 'upc', 'sku', 'plu', 'itemcode', 'productcode'];
const OLD_HINTS = ['old', 'original', 'current', 'existing', 'previous', 'prior', 'from', 'before'];
const NEW_HINTS = ['new', 'replacement', 'replace', 'updated', 'target', 'to', 'after', 'changeto'];

const aliasesFor = (hints) => new Set([
  ...hints.flatMap((hint) => CODE_WORDS.map((word) => `${hint}${word}`)),
  ...hints.flatMap((hint) => CODE_WORDS.map((word) => `${word}${hint}`)),
]);

const OLD_ALIASES = aliasesFor(OLD_HINTS);
const NEW_ALIASES = aliasesFor(NEW_HINTS);
const PRICE_ALIASES = new Set([
  'price', 'newprice', 'unitprice', 'sellingprice', 'retailprice',
  'pricepercont', 'pricepercontainer', 'containerprice', 'priceeach',
]);

const containsAny = (value, parts) => parts.some((part) => value.includes(part));
const isCodeLike = (value) => containsAny(value, CODE_WORDS);

const scoreHeader = (header, role) => {
  const normalized = normalizeHeader(header);
  if (!normalized) return 0;
  const aliases = role === 'old' ? OLD_ALIASES : NEW_ALIASES;
  const hints = role === 'old' ? OLD_HINTS : NEW_HINTS;
  const oppositeHints = role === 'old' ? NEW_HINTS : OLD_HINTS;
  if (aliases.has(normalized)) return 100;
  if (isCodeLike(normalized) && containsAny(normalized, hints)) return 70;
  if (containsAny(normalized, oppositeHints)) return 0;
  return 0;
};

const uniqueBestIndex = (headers, role) => {
  const scores = headers.map((header) => scoreHeader(header, role));
  const best = Math.max(...scores, 0);
  if (!best) return null;
  const matches = scores.reduce((indexes, score, index) => (
    score === best ? [...indexes, index] : indexes
  ), []);
  return matches.length === 1 ? matches[0] : null;
};

const uniquePriceIndex = (headers) => {
  const scores = headers.map((header) => {
    const normalized = normalizeHeader(header);
    if (PRICE_ALIASES.has(normalized)) return 100;
    if (normalized.includes('price') && (normalized.includes('cont') || normalized.includes('unit'))) return 70;
    return 0;
  });
  const best = Math.max(...scores, 0);
  if (!best) return null;
  const matches = scores.reduce((indexes, score, index) => (
    score === best ? [...indexes, index] : indexes
  ), []);
  return matches.length === 1 ? matches[0] : null;
};

export const autoMapColumns = (headers = []) => {
  const newIndex = uniqueBestIndex(headers, 'new');
  let oldIndex = uniqueBestIndex(headers, 'old');
  let priceIndex = uniquePriceIndex(headers);

  if (oldIndex === null && newIndex !== null) {
    const generic = headers.reduce((indexes, header, index) => {
      const normalized = normalizeHeader(header);
      const isGeneric = CODE_WORDS.includes(normalized);
      return isGeneric && index !== newIndex ? [...indexes, index] : indexes;
    }, []);
    if (generic.length === 1) oldIndex = generic[0];
  }

  if (oldIndex !== null && oldIndex === newIndex) {
    return { oldIndex: null, newIndex: null, priceIndex };
  }
  if (priceIndex === oldIndex || priceIndex === newIndex) priceIndex = null;
  return { oldIndex, newIndex, priceIndex };
};

export const parseWorkbookData = (data) => {
  const workbook = XLSX.read(data, { type: 'array', cellDates: false, cellText: true });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) throw new Error('The file does not contain a worksheet');
  const matrix = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], {
    header: 1,
    raw: false,
    defval: '',
    blankrows: true,
  });
  const headerOffset = matrix.findIndex((row) => row.some((value) => String(value).trim() !== ''));
  if (headerOffset < 0) throw new Error('The file is empty');

  const headers = matrix[headerOffset].map((value, index) => (
    String(value).trim() || `Column ${index + 1}`
  ));
  const records = matrix
    .slice(headerOffset + 1)
    .map((values, index) => ({
      rowId: `row-${headerOffset + index + 2}`,
      sourceRow: headerOffset + index + 2,
      values: headers.map((_, columnIndex) => String(values[columnIndex] ?? '')),
    }))
    .filter((record) => record.values.some((value) => value.trim() !== ''));

  if (!records.length) throw new Error('The file does not contain any data rows');
  return { headers, records };
};

export const parseCodeChangeFile = async (file) => {
  const extension = file?.name?.split('.').pop()?.toLowerCase();
  if (!['csv', 'xls', 'xlsx'].includes(extension)) {
    throw new Error('Choose a CSV, XLS, or XLSX file');
  }
  return parseWorkbookData(await file.arrayBuffer());
};

export const mapRecordsToCodeChanges = (records, oldIndex, newIndex, priceIndex) => records.map((record) => ({
  rowId: record.rowId,
  sourceRow: record.sourceRow,
  oldCode: record.values[oldIndex] ?? '',
  newCode: record.values[newIndex] ?? '',
  price: record.values[priceIndex] ?? '',
}));

const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

export const errorRowsToCsv = (rows) => [
  ['Source Row', 'Old Code', 'New Code', 'Price', 'Errors'].map(csvCell).join(','),
  ...rows.map((row) => [
    row.sourceRow,
    row.oldCode,
    row.newCode,
    row.price,
    (row.errors || []).map((error) => error.message || error).join('; '),
  ].map(csvCell).join(',')),
].join('\r\n');
